import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getPublishedTrips } from '../services/firestore/trips';
import { MapPin, X, Filter } from 'lucide-react';
import Header from '../components/Header';
import TripCard from '../components/TripCard';

const BrowseListings = () => {
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  
  // Filter states (non-functional for now)
  const [selectedDestinations, setSelectedDestinations] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedGoals, setSelectedGoals] = useState([]);
  const [selectedStyles, setSelectedStyles] = useState([]);
  const [priceRange, setPriceRange] = useState([0, 10000]);
  const [durationRange, setDurationRange] = useState([1, 30]);

  useEffect(() => {
    const loadTrips = async () => {
      try {
        const data = await getPublishedTrips();
        setTrips(data);
      } catch (error) {
        console.error('Error loading trips:', error);
      } finally {
        setLoading(false);
      }
    };
    loadTrips();
  }, []);

  const destinations = ['Thailand', 'Spain', 'India', 'Italy', 'Andalusia', 'Canary Islands', 'Costa Rica', 'Bali'];
  const categories = ['Wellness & Transformation', 'Fitness', 'Yoga', 'Meditation', 'Adventure', 'Digital Detox'];
  const goals = ['Strength & Conditioning', 'Body Transformation', 'Skill Learning', 'General Fitness', 'Mental Health', 'Weight Loss'];
  const styles = ['Bootcamp', 'Pilates', 'Bodyweight Training', 'Kettlebell Training', 'Yoga', 'Meditation'];

  const toggleFilter = (filterArray, setFilterArray, value) => {
    if (filterArray.includes(value)) {
      setFilterArray(filterArray.filter(item => item !== value));
    } else {
      setFilterArray([...filterArray, value]);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50/20 via-white to-green-50/10">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Mobile Filter Toggle */}
        <div className="lg:hidden mb-4">
          <button
            onClick={() => setIsFiltersOpen(!isFiltersOpen)}
            className="flex items-center gap-2 px-4 py-2 bg-white rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow"
          >
            <Filter className="w-5 h-5" />
            <span>Filters</span>
          </button>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Filters Sidebar */}
          <aside className={`lg:w-64 lg:sticky lg:top-20 lg:h-[calc(100vh-5rem)] ${
            isFiltersOpen ? 'block' : 'hidden lg:block'
          }`}>
            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-gray-200/50 p-6 h-full flex flex-col">
              {/* Mobile Close Button */}
              <div className="flex items-center justify-between mb-6 lg:hidden flex-shrink-0">
                <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
                <button
                  onClick={() => setIsFiltersOpen(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto pr-2 -mr-2">
                {/* Destinations */}
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Destinations</h4>
                  <div className="grid grid-cols-1 gap-2">
                    {destinations.map((dest) => (
                      <label key={dest} className="flex items-center gap-3 cursor-pointer hover:bg-emerald-50 p-2.5 rounded-lg transition-colors group">
                        <input
                          type="checkbox"
                          checked={selectedDestinations.includes(dest)}
                          onChange={() => toggleFilter(selectedDestinations, setSelectedDestinations, dest)}
                          className="w-4 h-4 text-emerald-600 rounded border-gray-300 focus:ring-emerald-500 focus:ring-2"
                        />
                        <span className="text-sm text-gray-700 group-hover:text-emerald-700 transition-colors">{dest}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Categories */}
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Categories</h4>
                  <div className="grid grid-cols-1 gap-2">
                    {categories.map((cat) => (
                      <label key={cat} className="flex items-center gap-3 cursor-pointer hover:bg-emerald-50 p-2.5 rounded-lg transition-colors group">
                        <input
                          type="checkbox"
                          checked={selectedCategories.includes(cat)}
                          onChange={() => toggleFilter(selectedCategories, setSelectedCategories, cat)}
                          className="w-4 h-4 text-emerald-600 rounded border-gray-300 focus:ring-emerald-500 focus:ring-2"
                        />
                        <span className="text-sm text-gray-700 group-hover:text-emerald-700 transition-colors">{cat}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Goals */}
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Goals</h4>
                  <div className="grid grid-cols-1 gap-2">
                    {goals.map((goal) => (
                      <label key={goal} className="flex items-center gap-3 cursor-pointer hover:bg-emerald-50 p-2.5 rounded-lg transition-colors group">
                        <input
                          type="checkbox"
                          checked={selectedGoals.includes(goal)}
                          onChange={() => toggleFilter(selectedGoals, setSelectedGoals, goal)}
                          className="w-4 h-4 text-emerald-600 rounded border-gray-300 focus:ring-emerald-500 focus:ring-2"
                        />
                        <span className="text-sm text-gray-700 group-hover:text-emerald-700 transition-colors">{goal}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Styles */}
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Style</h4>
                  <div className="grid grid-cols-1 gap-2">
                    {styles.map((style) => (
                      <label key={style} className="flex items-center gap-3 cursor-pointer hover:bg-emerald-50 p-2.5 rounded-lg transition-colors group">
                        <input
                          type="checkbox"
                          checked={selectedStyles.includes(style)}
                          onChange={() => toggleFilter(selectedStyles, setSelectedStyles, style)}
                          className="w-4 h-4 text-emerald-600 rounded border-gray-300 focus:ring-emerald-500 focus:ring-2"
                        />
                        <span className="text-sm text-gray-700 group-hover:text-emerald-700 transition-colors">{style}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Price Range */}
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Price Range</h4>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        placeholder="Min"
                        value={priceRange[0]}
                        onChange={(e) => setPriceRange([parseInt(e.target.value) || 0, priceRange[1]])}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      />
                      <span className="text-gray-500 font-medium">-</span>
                      <input
                        type="number"
                        placeholder="Max"
                        value={priceRange[1]}
                        onChange={(e) => setPriceRange([priceRange[0], parseInt(e.target.value) || 10000])}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Duration Range */}
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Duration (days)</h4>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        placeholder="Min"
                        value={durationRange[0]}
                        onChange={(e) => setDurationRange([parseInt(e.target.value) || 1, durationRange[1]])}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      />
                      <span className="text-gray-500 font-medium">-</span>
                      <input
                        type="number"
                        placeholder="Max"
                        value={durationRange[1]}
                        onChange={(e) => setDurationRange([durationRange[0], parseInt(e.target.value) || 30])}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Clear Filters - Fixed at bottom */}
              <div className="pt-4 border-t border-gray-200 flex-shrink-0">
                {(selectedDestinations.length > 0 || selectedCategories.length > 0 || selectedGoals.length > 0 || selectedStyles.length > 0) && (
                  <button
                    onClick={() => {
                      setSelectedDestinations([]);
                      setSelectedCategories([]);
                      setSelectedGoals([]);
                      setSelectedStyles([]);
                      setPriceRange([0, 10000]);
                      setDurationRange([1, 30]);
                    }}
                    className="w-full px-4 py-2.5 text-sm font-medium text-emerald-700 hover:bg-emerald-50 rounded-lg transition-colors border border-emerald-200 hover:border-emerald-300"
                  >
                    Clear all filters
                  </button>
                )}
              </div>
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1">
            {/* Header */}
            <div className="mb-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
                <div>
                  <h1 className="text-2xl sm:text-3xl font-light text-gray-900 mb-2">
                    Travel Experiences
                  </h1>
                  <p className="text-gray-600">
                    Showing {trips.length} results
                  </p>
                </div>
                <select className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent bg-white">
                  <option>Recommended</option>
                  <option>Price: Low to High</option>
                  <option>Price: High to Low</option>
                  <option>Duration: Short to Long</option>
                  <option>Duration: Long to Short</option>
                </select>
              </div>

              {/* Active Filters */}
              {(selectedDestinations.length > 0 || selectedCategories.length > 0 || selectedGoals.length > 0 || selectedStyles.length > 0) && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {selectedDestinations.map((dest) => (
                    <span key={dest} className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm">
                      {dest}
                      <button
                        onClick={() => setSelectedDestinations(selectedDestinations.filter(d => d !== dest))}
                        className="hover:bg-emerald-200 rounded-full p-0.5"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                  {selectedCategories.map((cat) => (
                    <span key={cat} className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm">
                      {cat}
                      <button
                        onClick={() => setSelectedCategories(selectedCategories.filter(c => c !== cat))}
                        className="hover:bg-emerald-200 rounded-full p-0.5"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                  {selectedGoals.map((goal) => (
                    <span key={goal} className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm">
                      {goal}
                      <button
                        onClick={() => setSelectedGoals(selectedGoals.filter(g => g !== goal))}
                        className="hover:bg-emerald-200 rounded-full p-0.5"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                  {selectedStyles.map((style) => (
                    <span key={style} className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm">
                      {style}
                      <button
                        onClick={() => setSelectedStyles(selectedStyles.filter(s => s !== style))}
                        className="hover:bg-emerald-200 rounded-full p-0.5"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                  <button
                    onClick={() => {
                      setSelectedDestinations([]);
                      setSelectedCategories([]);
                      setSelectedGoals([]);
                      setSelectedStyles([]);
                    }}
                    className="text-sm text-emerald-700 hover:text-emerald-800 underline"
                  >
                    Clear all
                  </button>
                </div>
              )}
            </div>

            {/* Listings Grid */}
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="animate-spin rounded-full h-12 w-12 border-2 border-emerald-600 border-t-transparent"></div>
              </div>
            ) : trips.length === 0 ? (
              <div className="text-center py-16">
                <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-12 border border-gray-200/50 max-w-md mx-auto shadow-lg">
                  <MapPin className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 font-light text-lg">
                    No listings available at the moment.
                  </p>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                {trips.map((trip) => (
                  <TripCard
                    key={trip.id}
                    trip={trip}
                    variant="default"
                    showCalendar={false}
                  />
                ))}
              </div>
            )}
          </main>
        </div>
      </div>
    </div>
  );
};

export default BrowseListings;

